import { Component } from '@theme/component';

/**
 * DAVIDsTEA featured tabs.
 *
 * The section renders every panel server-side and hides all but the first. This component only
 * moves the visibility around, so the products are in the HTML whether or not the script runs
 * and whether or not it runs in time.
 *
 * Keyboard behaviour follows the tabs pattern: one stop in the tab order for the whole strip,
 * arrows to move between tabs, Home and End to jump to the ends. Selection follows focus, which
 * is the right call here because switching panels costs nothing — nothing is fetched.
 */
export class DtFeaturedTabs extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { tablist } = this.refs;
    const panels = this.#panels;

    // A band with a single panel, or none, has nothing to switch between. It keeps what the
    // server rendered rather than being handed a strip of controls that do nothing.
    if (!tablist || panels.length < 2) return;

    this.#buildTabs(panels);

    tablist.addEventListener('click', this.#onClick);
    tablist.addEventListener('keydown', this.#onKeydown);
    tablist.addEventListener('pointerover', this.#warmHiddenPanels);
    tablist.addEventListener('focusin', this.#warmHiddenPanels);

    // The stylesheet shows the first panel until this attribute lands, at which point the
    // `hidden` attribute takes over. Selecting first, marking ready second, so the two never
    // disagree for a frame.
    this.#select(0);
    this.setAttribute('data-ready', '');
  }

  /** @returns {HTMLElement[]} */
  get #panels() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tabpanel"]')]);
  }

  /**
   * Builds the strip from the panels themselves. The label lives on the panel because a section
   * cannot read the settings of a theme block — see blocks/dt-featured-tab.
   *
   * @param {HTMLElement[]} panels
   */
  #buildTabs(panels) {
    const fragment = document.createDocumentFragment();

    panels.forEach((panel, index) => {
      const tab = document.createElement('button');
      tab.className = 'dt-featured-tabs__tab';
      tab.type = 'button';
      tab.setAttribute('role', 'tab');
      tab.id = panel.dataset.tabId || `${panel.id}-tab`;
      tab.setAttribute('aria-controls', panel.id);
      tab.setAttribute('aria-selected', String(index === 0));
      tab.tabIndex = index === 0 ? 0 : -1;
      tab.textContent = panel.dataset.tabLabel ?? '';

      panel.setAttribute('aria-labelledby', tab.id);
      fragment.append(tab);
    });

    this.refs.tablist.replaceChildren(fragment);
  }

  disconnectedCallback() {
    this.refs.tablist?.removeEventListener('click', this.#onClick);
    this.refs.tablist?.removeEventListener('keydown', this.#onKeydown);
    this.refs.tablist?.removeEventListener('pointerover', this.#warmHiddenPanels);
    this.refs.tablist?.removeEventListener('focusin', this.#warmHiddenPanels);

    super.disconnectedCallback();
  }

  /**
   * A panel that is not on screen is `display: none`, so its cards never intersect a viewport
   * and their `loading="lazy"` images are never fetched. Left alone, the first switch swaps in
   * a grid of empty frames and the photographs land one after the other behind the fade — the
   * pop the fade exists to remove.
   *
   * So they are fetched on intent instead: the first time the pointer or the focus reaches the
   * strip, which on a phone is the pointerdown that precedes the click. Someone who never goes
   * near the tabs downloads nothing extra. Setting `loading` to eager on an image already in
   * the document is what releases a load the browser is holding back.
   */
  #warmHiddenPanels = () => {
    this.refs.tablist?.removeEventListener('pointerover', this.#warmHiddenPanels);
    this.refs.tablist?.removeEventListener('focusin', this.#warmHiddenPanels);

    for (const image of this.querySelectorAll('[role="tabpanel"][hidden] img[loading="lazy"]')) {
      if (image instanceof HTMLImageElement) image.loading = 'eager';
    }
  };

  /** @returns {HTMLButtonElement[]} */
  get #tabs() {
    return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);
  }

  get #index() {
    return this.#tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const tab = /** @type {HTMLElement} */ (event.target)?.closest('[role="tab"]');
    if (!tab) return;

    this.#select(this.#tabs.indexOf(/** @type {HTMLButtonElement} */ (tab)), { animate: true });
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const tabs = this.#tabs;
    if (tabs.length < 2) return;

    const current = this.#index;
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#select(next, { animate: true });
    tabs[next]?.focus();
  };

  /**
   * @param {number} index
   * @param {{ animate?: boolean }} [options] `animate` marks the arriving panel with
   *   `data-entering`, which is the only thing the stylesheet's fade hangs on. It is passed by
   *   the click and the key handlers and never by the initial selection: the first panel is on
   *   screen from the first paint, and fading it in there would be an entrance nobody asked
   *   for. Nothing is marked when the shopper picks the tab that is already open either.
   */
  #select(index, { animate = false } = {}) {
    const tabs = this.#tabs;
    if (index < 0 || index >= tabs.length) return;

    const entering = animate && index !== this.#index;

    tabs.forEach((tab, i) => {
      const selected = i === index;
      const panel = this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls') ?? '')}`);

      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;

      if (!(panel instanceof HTMLElement)) return;

      panel.hidden = !selected;

      // The outgoing panel is hidden in this same task, so it is never laid out carrying the
      // attribute — style is computed once, at the end. The animation itself restarts on its
      // own each time: it is on an element coming back from `display: none`.
      if (selected && entering) panel.setAttribute('data-entering', '');
      else panel.removeAttribute('data-entering');
    });
  }
}

if (!customElements.get('dt-featured-tabs-component')) {
  customElements.define('dt-featured-tabs-component', DtFeaturedTabs);
}
